"use client";

import { useEffect, useLayoutEffect, useRef } from "react";
import {
  capturePostHogEvent,
  registerPostHogSuperProperties,
} from "@/lib/posthog";
import { ensureHomeVariant, type HomeVariant } from "@/lib/home-variant";

/**
 * `useLayoutEffect` runs after DOM mutation but BEFORE the browser paints, so a
 * soft navigation that needs a late assignment still switches layout without a
 * visible flash of the wrong arm. It warns when run during SSR, hence the swap.
 */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Guarantees the visitor has an experiment arm, and records it.
 *
 * Three jobs:
 *
 *  1. Backstops the inline assignment script. That script only runs on a full
 *    page load — React neuters `<script>` elements it creates on the client —
 *    so a soft navigation to the homepage would otherwise arrive unstamped and
 *    fall through to arm A. See `ensureHomeVariant`.
 *
 *  2. Registers `home_variant` as a PostHog SUPER PROPERTY, so it rides along
 *     on every subsequent event from this visitor — including ones fired deep
 *     in the funnel by components that know nothing about the experiment
 *     (`cake_product_viewed`, `order_started`). Without this there is no way to
 *     compare arms on anything past the first click.
 *
 *  3. Emits an explicit exposure event. Arm membership alone is not the
 *     denominator — only people who actually loaded the homepage belong in the
 *     analysis, and the cookie outlives the visit that set it.
 *
 * Mounted on the homepage only, deliberately: bucketing every visitor site-wide
 * would fill both arms with people who never saw the page under test.
 */
export function HomeVariantTracker() {
  const assigned = useRef<HomeVariant | null>(null);

  // Before paint, so a late assignment does not flash the control layout.
  useIsomorphicLayoutEffect(() => {
    assigned.current = ensureHomeVariant();
  }, []);

  const reported = useRef(false);

  useEffect(() => {
    // Strict Mode double-invokes effects in development. A genuine remount
    // (navigating away and back) gets a fresh ref and so still reports.
    if (reported.current) return;
    reported.current = true;

    const variant = assigned.current ?? ensureHomeVariant();
    // Registered BEFORE the exposure event so the event itself carries the arm.
    registerPostHogSuperProperties({ home_variant: variant });
    capturePostHogEvent("home_experiment_exposed", {
      home_variant: variant,
      experiment: "homepage_grid_vs_rails",
    });
    // Deliberately fires per homepage view rather than once per session: a
    // repeat view IS a repeat exposure. Read the experiment out over unique
    // sessions or persons, never over the raw exposure count.
  }, []);

  return null;
}
